'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BlogPostForm, type BlogPostFormRef } from '@/components/partials/blog-posts'
import { ContentError, FormSkeleton } from '@/components/partials'
import { useBlogPost, useUpdateBlogPost } from '@/hooks'
import { type CreateBlogPostParams } from '@/types/blog-post'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const blogPostFormRef = useRef<BlogPostFormRef>(null)

  const { data: blogPost, isLoading, error } = useBlogPost(id)
  const { mutate: updateBlogPost, isPending } = useUpdateBlogPost()

  const handleSubmit = (data: CreateBlogPostParams) => {
    updateBlogPost(
      { id, params: data },
      {
        onSuccess: async updatedBlogPost => {
          setIsRedirecting(true)
          queryClient.setQueryData(['blog-post', id], updatedBlogPost)
          await queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
          toast.success(t('updated_successfully'))
          router.push(`/blog-posts/${updatedBlogPost.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (blogPostFormRef.current) {
        blogPostFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/blog-posts/${id}`)
  }

  if (isLoading || !blogPost) {
    return <FormSkeleton fieldRows={1} showPictures showFiles />
  }

  if (error || !blogPost) {
    return <ContentError error={error} listUrl="/blog-posts" />
  }

  return (
    <BlogPostForm
      ref={blogPostFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={blogPost}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
